import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import date from 'date-and-time';
import { faArrowTrendUp } from '@fortawesome/pro-light-svg-icons/faArrowTrendUp';
import { TypedMemo } from '../../../common/types';
import { useTracksTopGaining } from '../../hooks/useTracksTopGainingHook';
import { useSMLDatePickerDateParam } from '../../hooks/useSMLDatePickerDateParam';
import { SMLResultsCard } from '../SMLResultsCard/SMLResultsCard';
import { SMLResultsItem } from '../SMLResultsCard/props';
import { useSMLPaginationParam } from '../../hooks/useSMLPaginationParam';
import { useSMLCountryParam } from '../../hooks/useSMLCountryParam';
import { useSMLGenreParam } from '../../hooks/useSMLGenreParam';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { countryParamName } from '../SMLCountrySelector/consts';
import { genreParamName } from '../SMLGenreSelector/consts';
import { TopGainingTracksCardProps } from './props';
import { ValueChangeElement } from '../PositionChangeElement';

const paginationParamName = 'gainingPage';

const TopGainingTracksCardComponent: React.FC<TopGainingTracksCardProps> = ({ artistId }) => {
  const intl = useIntl();
  const { selectedDate } = useSMLDatePickerDateParam();
  const { currentPage } = useSMLPaginationParam(paginationParamName);
  const { countryCode } = useSMLCountryParam();
  const { genre } = useSMLGenreParam();

  const {
    data: tracksData,
    isLoading,
    error,
    refetch,
  } = useTracksTopGaining({
    page: currentPage,
    date: selectedDate,
    artistId,
  });

  const tracks = useMemo(() => tracksData?.tracks || [], [tracksData?.tracks]);

  const items: SMLResultsItem[] = useMemo(
    () =>
      tracks.map((track) => ({
        id: track.trackId,
        imageUrl: track.imageUrl,
        name: `${track.mainTrackName}${track.artistNames.length > 0 ? ` - ${track.artistNames.join(', ')}` : ''}`,
        position: track.songAddedPosition.current,
        rightElement: <ValueChangeElement currentValue={track.addedToPlaylists.current} />,
        chart: { entityType: 'track' as const, date: selectedDate },
        route: SMLRoutes.trackDetails,
        params: {
          trackId: track.trackId,
          date: date.format(selectedDate, 'YYYY-MM-DD'),
          ...(countryCode !== undefined ? { [countryParamName]: countryCode } : {}),
          ...(genre !== undefined ? { [genreParamName]: genre } : {}),
        },
      })),
    [tracks, selectedDate, countryCode, genre]
  );

  const title = artistId
    ? intl.formatMessage({
        id: 'app.sml.artist.topGainingTracks.title',
        defaultMessage: 'Top Gaining Tracks for Artist',
      })
    : intl.formatMessage({
        id: 'app.sml.home.topGainingTracks.title',
        defaultMessage: 'Top Gaining Tracks',
      });

  const subtitle = date.format(selectedDate, 'MMM DD, YYYY');

  return (
    <SMLResultsCard
      title={title}
      subtitle={subtitle}
      icon={faArrowTrendUp}
      iconColor="var(--pos)"
      items={items}
      isLoading={isLoading}
      error={error}
      onRetry={() => {
        refetch().catch(console.error);
      }}
      imageShape="rounded-square"
      showCountryFilter={artistId === undefined}
      showGenreFilter={artistId === undefined}
      paginationParamName={paginationParamName}
    />
  );
};

export const TopGainingTracksCard = TypedMemo(TopGainingTracksCardComponent);
